import { useConfiguratorStore, ARTWORK_POSITIONS, TEXT_LABELS } from '@/state/configuratorStore';
import { resolveTextStyle, type ArtworkPositionId, type TextStyleKey } from '@/lib/designState';

const TEXT_KEYS: TextStyleKey[] = ['team', 'name', 'number'];

const BACK_POS: { id: ArtworkPositionId; label: string }[] = [
  { id: 'back-top', label: 'Top' },
  { id: 'back-middle', label: 'Middle' },
  { id: 'back-bottom', label: 'Bottom' },
  { id: 'back-left', label: 'Left' },
  { id: 'back-right', label: 'Right' },
];

const FRONT_POS: { id: ArtworkPositionId; label: string }[] = [
  { id: 'front-top-left', label: 'Top L' },
  { id: 'front-top-center', label: 'Top C' },
  { id: 'front-top-right', label: 'Top R' },
  { id: 'front-center', label: 'Center' },
  { id: 'front-bottom', label: 'Bottom' },
];

const SLEEVE_POS: { id: ArtworkPositionId; label: string }[] = [
  { id: 'sleeve-left', label: 'L mid' },
  { id: 'sleeve-right', label: 'R mid' },
  { id: 'sleeve-left-top', label: 'L top' },
  { id: 'sleeve-right-top', label: 'R top' },
  { id: 'sleeve-left-bottom', label: 'L cuff' },
  { id: 'sleeve-right-bottom', label: 'R cuff' },
];

export function AdjustHud() {
  const ready = useConfiguratorStore(s => s.ready);
  const selectedPrintId = useConfiguratorStore(s => s.selectedPrintId);
  const placingId = useConfiguratorStore(s => s.placingId);
  const artworks = useConfiguratorStore(s => s.artworks);
  const personalisation = useConfiguratorStore(s => s.personalisation);
  const cancelPlace = useConfiguratorStore(s => s.cancelPlace);
  const beginPlace = useConfiguratorStore(s => s.beginPlace);
  const nudgePrint = useConfiguratorStore(s => s.nudgePrint);
  const scalePrint = useConfiguratorStore(s => s.scalePrint);
  const setTextStyle = useConfiguratorStore(s => s.setTextStyle);
  const updateArtwork = useConfiguratorStore(s => s.updateArtwork);
  const selectPrint = useConfiguratorStore(s => s.selectPrint);
  const focusPrint = useConfiguratorStore(s => s.focusPrint);

  if (!ready || !selectedPrintId) return null;

  const isText = selectedPrintId.startsWith('text:');
  const textKey = isText ? (selectedPrintId.slice(5) as TextStyleKey) : null;
  const validText = textKey && TEXT_KEYS.includes(textKey) ? textKey : null;
  const art = !isText ? artworks.find(a => a.id === selectedPrintId) : null;

  if (!validText && !art) return null;

  const title = validText ? TEXT_LABELS[validText] : art!.name;
  const side = validText
    ? 'BACK'
    : art!.side === 'back'
      ? 'BACK'
      : art!.position.startsWith('sleeve')
        ? 'SLEEVE'
        : 'FRONT';
  const place = validText
    ? 'Back panel'
    : art!.position === 'custom'
      ? 'Free'
      : (ARTWORK_POSITIONS[art!.position]?.label ?? art!.position);

  const posList = validText
    ? BACK_POS
    : art!.side === 'back'
      ? BACK_POS
      : art!.position.startsWith('sleeve') || art!.mirrorSleeves
        ? [...FRONT_POS, ...SLEEVE_POS]
        : FRONT_POS;

  const applyPos = (id: ArtworkPositionId) => {
    if (validText) {
      const a = ARTWORK_POSITIONS[id];
      setTextStyle(validText, { x: a.x, y: a.y });
      focusPrint(`text:${validText}`);
      return;
    }
    if (art) {
      updateArtwork(art.id, { position: id });
      // focusPrint runs after store update via queueMicrotask in updateArtwork;
      // also focus immediately for sleeve rotation when already selected
      requestAnimationFrame(() => focusPrint(art.id));
    }
  };

  const currentPos = validText ? null : art!.position;

  return (
    <div className="adjust-hud" role="toolbar" aria-label="Quick adjust">
      <div className="adjust-hud__head">
        <div className="adjust-hud__title">
          <strong>{title}</strong>
          <span className={`loc-badge loc-badge--${side === 'BACK' ? 'back' : 'front'}`}>
            {side}
          </span>
        </div>
        <button
          type="button"
          className="adjust-hud__close"
          onClick={() => selectPrint(null)}
          aria-label="Close adjust"
        >
          ×
        </button>
      </div>

      <div className="adjust-hud__where">{place}</div>

      <div className="adjust-hud__section">
        <span className="adjust-hud__label">Position</span>
        <div className="adjust-hud__chips">
          {posList.map(p => (
            <button
              key={p.id}
              type="button"
              className={`hud-chip${currentPos === p.id ? ' is-active' : ''}`}
              onClick={() => applyPos(p.id)}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={`hud-chip${placingId === selectedPrintId ? ' is-armed' : ''}`}
            onClick={() => beginPlace(selectedPrintId)}
          >
            Click model
          </button>
        </div>
      </div>

      <div className="adjust-hud__row">
        <div className="adjust-hud__section adjust-hud__section--grow">
          <span className="adjust-hud__label">Move</span>
          <div className="hud-nudge">
            <button type="button" onClick={() => nudgePrint(-20, 0)} aria-label="Left">
              ←
            </button>
            <button type="button" onClick={() => nudgePrint(0, -20)} aria-label="Up">
              ↑
            </button>
            <button type="button" onClick={() => nudgePrint(0, 20)} aria-label="Down">
              ↓
            </button>
            <button type="button" onClick={() => nudgePrint(20, 0)} aria-label="Right">
              →
            </button>
          </div>
        </div>
        <div className="adjust-hud__section adjust-hud__section--grow">
          <span className="adjust-hud__label">Scale</span>
          <div className="hud-scale">
            <button type="button" onClick={() => scalePrint(0.9)}>
              −
            </button>
            <button type="button" onClick={() => scalePrint(1.1)}>
              +
            </button>
          </div>
        </div>
      </div>

      {placingId === selectedPrintId && (
        <div className="adjust-hud__placing">
          <span>Click garment…</span>
          <button type="button" onClick={cancelPlace}>
            Cancel
          </button>
        </div>
      )}

      {validText && (
        <div className="adjust-hud__section">
          <span className="adjust-hud__label">Size</span>
          <input
            type="range"
            className="hud-range"
            min={24}
            max={400}
            value={resolveTextStyle(validText, personalisation).fontSize}
            onChange={e => setTextStyle(validText, { fontSize: Number(e.target.value) })}
          />
        </div>
      )}

      {art && (
        <div className="adjust-hud__section">
          <span className="adjust-hud__label">Width</span>
          <input
            type="range"
            className="hud-range"
            min={40}
            max={800}
            value={art.w}
            onChange={e => {
              const w = Number(e.target.value);
              const ratio = art.h / Math.max(art.w, 1);
              updateArtwork(art.id, { w, h: Math.round(w * ratio) });
            }}
          />
        </div>
      )}
    </div>
  );
}
